import { useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { logout } from '../../lib/auth.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { UserAvatar, type UserAvatarSource } from '../../lib/avatars.js';

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §01 منوی حساب کاربری.
 *
 * `userName`/`userRole` are real now (First Login Profile's `GET
 * /api/v1/me`, passed down from AuthenticatedShell.tsx — not invented
 * locally here), falling back to a generic placeholder only while that
 * fetch is loading or genuinely has no name on file.
 *
 * Three of the board's five menu rows have a real destination:
 * "حساب کاربری" (/account, the full profile-settings page), "امنیت و
 * نشست‌ها" (/admin/security, real) and "خروج از حساب" (real,
 * apps/client/src/lib/auth.ts's logout()). "تنظیمات شخصی" is folded into
 * /account (same page, no separate route) and "تغییر فضای کاری" has no
 * backing concept — this app has no multi-tenant-per-account switch
 * anywhere in the repo — so it stays omitted rather than linked to nothing.
 */
export function UserMenuPopover({
  slug,
  userName,
  userRole,
  avatar,
}: {
  slug: string;
  userName: string;
  userRole: string;
  avatar: UserAvatarSource | null;
}) {
  const [signingOut, setSigningOut] = useState(false);
  const base = tenantBasePath(slug);

  async function onLogout() {
    setSigningOut(true);
    try {
      await logout(slug);
    } finally {
      window.location.href = `${base}/login`;
    }
  }

  return (
    <div>
      <div
        style={{
          padding: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid var(--sk-divider)',
        }}
      >
        <UserAvatar name={userName} source={avatar} size={40} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{userName}</div>
          <div style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>{userRole}</div>
        </div>
      </div>
      <div style={{ padding: 6 }}>
        <a href={`${base}/account`} className="sk-menu-item" style={{ textDecoration: 'none' }}>
          <Icon name="user" size="md" />
          حساب کاربری
        </a>
        <a
          href={`${base}/admin/security`}
          className="sk-menu-item"
          style={{ textDecoration: 'none' }}
        >
          <Icon name="shield" size="md" />
          امنیت و نشست‌ها
        </a>
      </div>
      <div style={{ borderTop: '1px solid var(--sk-divider)', padding: 6 }}>
        <button
          type="button"
          className="sk-menu-item sk-menu-item--danger"
          disabled={signingOut}
          onClick={onLogout}
          style={{ width: '100%' }}
        >
          <Icon name="logout" size="md" />
          خروج از حساب
        </button>
      </div>
    </div>
  );
}
